import {USER_KEYS} from "@/api/userApi";
import BuildingIcon from "@/assets/dark_gradient/building.svg?react";
import Button from "@/components/Common/Button";
import {client} from "@/httpClient";
import BrandLogo from "@/pages/profile/BrandLogo";
import {hasJustRegisteredAtom} from "@/store/jotaiAtoms";
import useRegistrationStore from "@/store/registration";
import useUserStore from "@/store/user";
import {useMutation} from "@tanstack/react-query";
import {createFileRoute, useNavigate} from "@tanstack/react-router";
import {useSetAtom} from "jotai";
import {useRef} from "react";
import {useTranslation} from "react-i18next";
import {useEffectOnce, useTitle} from "react-use";

export const Route = createFileRoute("/_authenticated/image-upload")({
  component: ImageUpload,
});

function ImageUpload() {
  const {t} = useTranslation("common");
  const navigate = useNavigate();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const resetRegistrationStore = useRegistrationStore(state => state.reset);
  const userId = useUserStore(state => state.user.id);
  const setJustRegistered = useSetAtom(hasJustRegisteredAtom);

  const proceedWithRegistration = () => {
    navigate({to: "/welcome", replace: true});
    resetRegistrationStore();
  };

  const {mutateAsync: uploadBio, isPending} = useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: USER_KEYS.mUser(userId),
    mutationFn: () =>
      textareaRef.current?.value
        ? client.patch(USER_KEYS.mUser(userId)[0], {aboutme: textareaRef.current?.value})
        : Promise.resolve({}),
  });

  useTitle(t("Upload Image"));

  useEffectOnce(() => {
    setJustRegistered(true);
  });

  return (
    <div className="max-w-wide container mx-auto mt-8 mb-20 flex w-full flex-col items-center gap-10 px-4 pb-10 sm:px-8">
      <div className="border-primary-light-solid flex items-center gap-5 rounded-full border px-5 py-2 md:gap-2">
        <BuildingIcon className="size-5 shrink-0" />
        <span className="bg-linear-(--primary-dark-gradient) bg-clip-text py-1 text-base leading-none font-semibold text-transparent md:text-lg">
          {t("provideFurtherProfileDetails")}
        </span>
      </div>
      <span className="text-neutral-black text-lg font-semibold">{t("addCompanyLogo")}</span>
      <BrandLogo />
      <div className="flex w-full flex-col items-center gap-4">
        <label
          className="text-neutral-black text-center text-base font-semibold md:text-start md:text-lg"
          htmlFor="bio">
          {t("writeBioDescription")}
        </label>
        <textarea
          id="bio"
          ref={textareaRef}
          className="border-primary-light-solid text-neutral-black placeholder:text-neutral-grey-light focus:border-primary-medium-solid flex h-36 w-full resize-none rounded-md border p-4 pr-0 outline-hidden transition-all duration-150 placeholder:text-sm md:placeholder:text-base"
          placeholder={t("bioPlaceholder")}
        />
      </div>

      <div className="flex gap-5">
        <Button buttonStyle="White" className="h-14 w-32 md:w-48" onClick={proceedWithRegistration}>
          {t("Skip")}
        </Button>
        <Button
          isLoading={isPending}
          className="h-14 w-32 md:w-48"
          buttonStyle="Blue"
          onClick={async () => {
            await uploadBio();
            proceedWithRegistration();
          }}>
          {t("next")}
        </Button>
      </div>
    </div>
  );
}
